import { useEffect, useRef, useState } from 'react';
import { message } from '../utils/antdMessage';
import { apiFetch } from '../utils/api';
import { extractMapUrlFromText } from '../utils/extractMapUrl';
import {
  DEFAULT_TARGET_FORM,
  defaultExpiresAtLocalInput,
  datetimeLocalToIso,
  parseCoordinate,
  parseCoordinateString,
  pickWgs84FromParseResult,
  resolveMissionMemberKeys,
  resolveMissionRecipientNames
} from '../utils/rescuePush';
import { normalizeRescuePushSettings } from '../utils/rescuePushSettings';
import siteConfig from '../config/site-config';

/**
 * 目标表单、坐标/地图链接解析、推送
 */
export default function useRescuePushTarget({ mapRef, markTargetOnMapRef, displayTargetOnMapRef }) {
  const [targetForm, setTargetForm] = useState({ ...DEFAULT_TARGET_FORM, expires_at: defaultExpiresAtLocalInput() });
  const [targetLoading, setTargetLoading] = useState(false);
  const [mapLinkParsing, setMapLinkParsing] = useState(false);
  const [targetResult, setTargetResult] = useState(null);
  const mapLinkParseTimerRef = useRef(null);

  useEffect(() => {
    fetch(`${siteConfig.getApiUrl()}/admin/ui-settings/public`)
      .then((response) => response.json())
      .then((data) => {
        const rescue = normalizeRescuePushSettings(data?.settings?.rescuePushSettings);
        setTargetForm((form) => ({
          ...form,
          icon: rescue.defaultIcon || form.icon,
          level: rescue.defaultLevel || form.level
        }));
      })
      .catch(() => {});
  }, []);

  const handleFormChange = (form, setForm) => (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleTargetPush = async () => {
    setTargetLoading(true);
    setTargetResult(null);

    try {
      const lat = parseFloat(targetForm.lat);
      const lng = parseFloat(targetForm.lng);

      if (!targetForm.lat.trim() && !targetForm.lng.trim() && !targetForm.map_link.trim()) {
        setTargetResult({ success: false, msg: '请输入坐标或地图链接' });
        setTargetLoading(false);
        return;
      }

      if (targetForm.lat.trim() && targetForm.lng.trim()) {
        if (isNaN(lat) || isNaN(lng)) {
          setTargetResult({ success: false, msg: '坐标格式无效，请检查输入' });
          setTargetLoading(false);
          return;
        }

        if (lat < -90 || lat > 90) {
          setTargetResult({ success: false, msg: '纬度范围应在 -90 到 90 之间' });
          setTargetLoading(false);
          return;
        }

        if (lng < -180 || lng > 180) {
          setTargetResult({ success: false, msg: '经度范围应在 -180 到 180 之间' });
          setTargetLoading(false);
          return;
        }
      }

      if (
        !targetForm.to_all &&
        !String(targetForm.mission_id || '').trim() &&
        targetForm.org_names.length === 0 &&
        targetForm.group_names.length === 0 &&
        targetForm.user_names.length === 0
      ) {
        setTargetResult({ success: false, msg: '请选择接收者、任务，或勾选「推送给所有人」' });
        setTargetLoading(false);
        return;
      }

      let missionPayload = null;
      const missionId = String(targetForm.mission_id || '').trim();
      if (missionId) {
        try {
          const { loadUserSettings, SETTING_KEYS } = await import('../utils/userSettings');
          const { migrateLegacyMissions } = await import('../utils/dashboard/missionsModel');
          const { loadCommandMissionState } = await import('../utils/dashboard/missionPersist');
          const settings = await loadUserSettings();
          const shared = await loadCommandMissionState(settings);
          const migrated = migrateLegacyMissions({
            missions: shared?.[SETTING_KEYS.MISSIONS],
            activeMissionId: shared?.[SETTING_KEYS.ACTIVE_MISSION_ID],
            missionMemberIds: shared?.[SETTING_KEYS.MISSION_MEMBER_IDS],
            missionGroups: shared?.[SETTING_KEYS.MISSION_GROUPS],
            missionMode: shared?.[SETTING_KEYS.MISSION_MODE]
          });
          const mission = (migrated.missions || []).find((m) => String(m.id) === missionId);
          if (!mission) {
            setTargetResult({ success: false, msg: '所选任务不存在或已结束，请刷新后重选' });
            setTargetLoading(false);
            return;
          }
          const memberKeys = resolveMissionMemberKeys(mission, targetForm.mission_group_id);
          if (!memberKeys.length) {
            setTargetResult({
              success: false,
              msg: targetForm.mission_group_id ? '该任务小组暂无队员' : '该任务名册暂无队员'
            });
            setTargetLoading(false);
            return;
          }
          let names = [];
          try {
            const resp = await apiFetch('/api/device/list?display_days=7');
            const data = await resp.json();
            names = resolveMissionRecipientNames(mission, targetForm.mission_group_id, data.devices || []);
          } catch (_) {}
          missionPayload = {
            mission_id: missionId,
            mission_group_id: String(targetForm.mission_group_id || '').trim() || null,
            mission_group_name: String(targetForm.mission_group_name || '').trim() || null,
            mission_member_keys: memberKeys,
            user_names: names
          };
        } catch (e) {
          setTargetResult({ success: false, msg: '读取任务名册失败: ' + (e.message || e) });
          setTargetLoading(false);
          return;
        }
      }


      const expiresIso = datetimeLocalToIso(targetForm.expires_at || defaultExpiresAtLocalInput());
      if (!expiresIso) {
        setTargetResult({ success: false, msg: '请填写有效的有效时间' });
        setTargetLoading(false);
        return;
      }
      if (new Date(expiresIso).getTime() <= Date.now() - 60 * 1000) {
        setTargetResult({ success: false, msg: '有效时间不能早于当前时间' });
        setTargetLoading(false);
        return;
      }

      const pushData = {
        ...targetForm,
        lat: targetForm.lat.trim() ? parseFloat(targetForm.lat) : null,
        lng: targetForm.lng.trim() ? parseFloat(targetForm.lng) : null,
        org_names: missionPayload ? [] : (targetForm.to_all ? [] : targetForm.org_names),
        group_names: missionPayload ? [] : (targetForm.to_all ? [] : targetForm.group_names),
        user_names: missionPayload
          ? (missionPayload.user_names || [])
          : (targetForm.to_all ? [] : targetForm.user_names),
        to_all: missionPayload ? false : !!targetForm.to_all,
        expires_at: expiresIso,
        mission_id: missionPayload ? missionPayload.mission_id : null,
        mission_group_id: missionPayload ? missionPayload.mission_group_id : null,
        mission_group_name: missionPayload ? missionPayload.mission_group_name : null,
        mission_member_keys: missionPayload ? missionPayload.mission_member_keys : undefined
      };

      console.log('推送目标数据:', pushData);

      const response = await apiFetch('/api/rescue/target', {
        method: 'POST',
        body: JSON.stringify(pushData)
      });
      const raw = await response.text();
      let data = {};
      try {
        data = JSON.parse(raw);
      } catch (_) {
        setTargetResult({
          success: false,
          msg: `推送失败（HTTP ${response.status}）：服务器返回非 JSON`
        });
        return;
      }
      if (response.ok && data.success) {
        setTargetResult({ success: true, msg: '推送成功', data: data.target });

        if (data.target) {
          displayTargetOnMapRef.current(data.target);
        }

        setTargetForm((f) => ({
          ...f,
          lat: '',
          lng: '',
          message: '',
          map_link: ''
        }));
      } else {
        const permHint = data.requiredPermission
          ? `（需要权限「${data.requiredPermission}」，请在管理后台给当前用户分组勾选该权限后重新登录）`
          : '';
        setTargetResult({
          success: false,
          msg: (data.error || `推送失败（HTTP ${response.status}）`) + (data.detail ? `：${data.detail}` : '') + permHint
        });
      }
    } catch (e) {
      console.error('推送目标失败:', e);
      setTargetResult({ success: false, msg: '推送失败: ' + e.message });
    } finally {
      setTargetLoading(false);
    }
  };

  const handleCoordinateParse = () => {
    const inputLat = targetForm.lat;
    const inputLng = targetForm.lng;

    if (!inputLat && !inputLng) {
      alert('请先输入经纬度坐标。');
      return;
    }

    let lat;
    let lng;

    const combinedCoords = parseCoordinateString(inputLat) || parseCoordinateString(inputLng);

    if (combinedCoords) {
      lat = combinedCoords.lat;
      lng = combinedCoords.lng;
    } else if (inputLat && inputLng) {
      lat = parseCoordinate(inputLat);
      lng = parseCoordinate(inputLng);
    } else {
      alert('请输入完整的经纬度坐标。');
      return;
    }

    if (isNaN(lat) || isNaN(lng)) {
      alert('坐标格式不正确，请检查输入。');
      return;
    }

    if (lat < -90 || lat > 90) {
      alert('纬度范围应在 -90 到 90 之间。');
      return;
    }

    if (lng < -180 || lng > 180) {
      alert('经度范围应在 -180 到 180 之间。');
      return;
    }

    setTargetForm((f) => ({
      ...f,
      lat: lat.toFixed(6),
      lng: lng.toFixed(6),
      map_link: ''
    }));

    if (mapRef.current) {
      if (mapRef.current.getSource('target-marker')) {
        mapRef.current.removeLayer('target-marker');
        mapRef.current.removeSource('target-marker');
      }

      mapRef.current.addSource('target-marker', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [lng, lat]
          },
          properties: {
            title: '目标位置'
          }
        }
      });

      mapRef.current.addLayer({
        id: 'target-marker',
        type: 'circle',
        source: 'target-marker',
        paint: {
          'circle-radius': 8,
          'circle-color': '#ff4444',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff'
        }
      });

      mapRef.current.flyTo({
        center: [lng, lat],
        zoom: 15
      });
    }

    alert(`已智能识别并填充经纬度：\n纬度: ${lat.toFixed(6)}\n经度: ${lng.toFixed(6)}`);
  };

  const parseMapLinkViaApi = async (mapLink) => {
    const res = await apiFetch('/api/parse-map-url', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url: mapLink })
    });
    const data = await res.json();
    if (!data.success) {
      throw new Error(data.error || '无法解析该地图链接');
    }
    const wgs84 = pickWgs84FromParseResult(data);
    if (!wgs84) {
      throw new Error('解析成功但未得到可用的 WGS84 坐标');
    }
    return {
      ...wgs84,
      extractedUrl: data.extractedUrl || mapLink
    };
  };

  const handleMapLinkParse = async (rawText) => {
    const sourceText = typeof rawText === 'string' ? rawText : targetForm.map_link;
    if (!String(sourceText || '').trim()) {
      message.warning('请输入地图链接，或粘贴包含链接的地址文本');
      return false;
    }

    const extractedUrl = extractMapUrlFromText(sourceText);
    if (!extractedUrl) {
      message.warning('未从内容中识别到地图链接，请确认包含 http(s):// 链接');
      return false;
    }

    setMapLinkParsing(true);
    try {
      const coords = await parseMapLinkViaApi(extractedUrl);
      const urlToKeep = coords.extractedUrl || extractedUrl;
      setTargetForm((f) => ({
        ...f,
        map_link: urlToKeep,
        lat: Number(coords.lat).toFixed(6),
        lng: Number(coords.lng).toFixed(6)
      }));
      markTargetOnMapRef.current(coords.lat, coords.lng, coords.name || '目标位置');
      const tipName = coords.name
        ? `已解析：${coords.name}（WGS84，已填入纬度/经度）`
        : '已解析为 WGS84 坐标并填入纬度/经度';
      message.success(
        String(sourceText).trim() !== urlToKeep ? `已从文本中提取链接并解析。${tipName}` : tipName
      );
      return true;
    } catch (error) {
      console.error('地图链接解析失败:', error);
      message.warning(error.message || '地图链接解析失败');
      return false;
    } finally {
      setMapLinkParsing(false);
    }
  };

  const handleCoordinatePaste = (e, field) => {
    const pastedText = e.clipboardData.getData('text');

    if (pastedText.includes(',') && /[\d.-]/.test(pastedText)) {
      e.preventDefault();

      const coords = parseCoordinateString(pastedText);
      if (coords) {
        setTargetForm((f) => ({
          ...f,
          lat: coords.lat.toFixed(6),
          lng: coords.lng.toFixed(6),
          map_link: ''
        }));

        if (mapRef.current) {
          if (mapRef.current.getSource('target-marker')) {
            mapRef.current.removeLayer('target-marker');
            mapRef.current.removeSource('target-marker');
          }

          mapRef.current.addSource('target-marker', {
            type: 'geojson',
            data: {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [coords.lng, coords.lat]
              },
              properties: {
                title: '目标位置'
              }
            }
          });

          mapRef.current.addLayer({
            id: 'target-marker',
            type: 'circle',
            source: 'target-marker',
            paint: {
              'circle-radius': 8,
              'circle-color': '#ff4444',
              'circle-stroke-width': 2,
              'circle-stroke-color': '#ffffff'
            }
          });

          mapRef.current.flyTo({
            center: [coords.lng, coords.lat],
            zoom: 15
          });
        }

        setTimeout(() => {
          alert(`已自动识别粘贴的坐标：\n纬度: ${coords.lat.toFixed(6)}\n经度: ${coords.lng.toFixed(6)}`);
        }, 100);
      }
    }
  };

  const handleMapLinkChange = (e) => {
    const mapLink = e.target.value;
    setTargetForm((f) => ({ ...f, map_link: mapLink }));

    if (mapLinkParseTimerRef.current) {
      clearTimeout(mapLinkParseTimerRef.current);
      mapLinkParseTimerRef.current = null;
    }

    const extractedUrl = extractMapUrlFromText(mapLink);
    if (!extractedUrl || mapLinkParsing) return;

    mapLinkParseTimerRef.current = setTimeout(() => {
      handleMapLinkParse(mapLink);
    }, 600);
  };

  return {
    targetForm,
    setTargetForm,
    targetLoading,
    mapLinkParsing,
    targetResult,
    setTargetResult,
    handleFormChange,
    handleTargetPush,
    handleCoordinateParse,
    handleCoordinatePaste,
    handleMapLinkParse,
    handleMapLinkChange
  };
}
